import React, { useState } from 'react';
import { Dropdown, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getNavIconClass } from '../../../navigation/clinicNavConfig';
import Portal from '../../common/Portal';

const APPOINTMENT_ACTION_ITEMS = [
    {
        key: 'reason',
        label: 'View Reason',
        icon: 'bi-card-text',
        theme: 'slate',
        action: 'reason',
    },
    {
        key: 'confirm',
        label: 'Confirm',
        icon: 'bi-check2-circle',
        theme: 'success',
        action: 'confirm',
    },
    {
        key: 'cancel',
        label: 'Cancel',
        icon: 'bi-x-circle',
        theme: 'danger',
        action: 'cancel',
        danger: true,
    },
    {
        key: 'patient',
        label: 'Open Patient File',
        icon: 'bi-person-vcard',
        theme: 'indigo',
        action: 'patient',
    },
];

function AppointmentActionIcon({ icon, theme = 'primary', size = 'sm' }) {
    return (
        <span
            className={`ecx-nav-icon ${getNavIconClass(theme)} appointments-action-icon${
                size === 'md' ? ' appointments-action-icon--md' : ''
            }`}
            aria-hidden="true"
        >
            <i className={`bi ${icon}`} />
        </span>
    );
}

function AppointmentActionsMenu({
    appt,
    updating = false,
    menuOpen,
    onMenuToggle,
    onConfirmCancel,
    usePortal = false,
}) {
    const [showReasonModal, setShowReasonModal] = useState(false);
    const reasonText = appt.reason?.trim() || 'No reason provided for this appointment.';
    const patient = appt.patient || {};
    const patientName =
        [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim() || 'Patient';

    const handleMenuToggle = (nextShow, meta) => {
        onMenuToggle?.(nextShow, meta);
    };

    const renderMenu = (
        <Dropdown.Menu
            className="appointments-actions-dropdown__menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {APPOINTMENT_ACTION_ITEMS.map((item) => {
                if (item.action === 'patient') {
                    return (
                        <Dropdown.Item
                            key={item.key}
                            as={Link}
                            to={`/patient-files/${appt.patientId}`}
                            className="appointments-actions-dropdown__item"
                        >
                            <AppointmentActionIcon icon={item.icon} theme={item.theme} />
                            <span>{item.label}</span>
                        </Dropdown.Item>
                    );
                }

                if (item.action === 'reason') {
                    return (
                        <Dropdown.Item
                            key={item.key}
                            as="button"
                            type="button"
                            className="appointments-actions-dropdown__item"
                            onClick={() => setShowReasonModal(true)}
                        >
                            <AppointmentActionIcon icon={item.icon} theme={item.theme} />
                            <span>{item.label}</span>
                        </Dropdown.Item>
                    );
                }

                return (
                    <Dropdown.Item
                        key={item.key}
                        as="button"
                        type="button"
                        disabled={updating}
                        className={`appointments-actions-dropdown__item${
                            item.danger ? ' appointments-actions-dropdown__item--danger' : ''
                        }`}
                        onClick={() => onConfirmCancel(item.action === 'confirm')}
                    >
                        <AppointmentActionIcon icon={item.icon} theme={item.theme} />
                        <span>{item.label}</span>
                    </Dropdown.Item>
                );
            })}
        </Dropdown.Menu>
    );

    return (
        <>
            <Dropdown
                align="end"
                className="appointments-actions-dropdown"
                {...(typeof menuOpen === 'boolean'
                    ? { show: menuOpen, onToggle: handleMenuToggle }
                    : {})}
            >
                <Dropdown.Toggle
                    variant="light"
                    size="sm"
                    className="appointments-actions-dropdown__toggle"
                    disabled={updating}
                    title="More actions"
                >
                    <i className="bi bi-three-dots" aria-hidden="true" />
                </Dropdown.Toggle>
                {usePortal ? <Portal>{renderMenu}</Portal> : renderMenu}
            </Dropdown>

            <Modal
                show={showReasonModal}
                onHide={() => setShowReasonModal(false)}
                centered
                className="appointments-reason-modal"
            >
                <Modal.Header className="appointments-reason-modal__header border-0">
                    <Modal.Title className="appointments-reason-modal__title">
                        <AppointmentActionIcon icon="bi-card-text" theme="slate" size="md" />
                        <span>Appointment Reason</span>
                    </Modal.Title>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__close-btn"
                        onClick={() => setShowReasonModal(false)}
                        aria-label="Close"
                    >
                        <i className="bi bi-x-lg" />
                    </button>
                </Modal.Header>
                <Modal.Body className="appointments-reason-modal__body pt-0">
                    <div className="appointments-reason-modal__meta">
                        <div className="appointments-reason-modal__patient">{patientName}</div>
                        <div className="appointments-reason-modal__time">
                            {appt.startTime} – {appt.endTime}
                        </div>
                    </div>
                    <p className="appointments-reason-modal__text">{reasonText}</p>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default AppointmentActionsMenu;
